import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiErr, del, fmtDate, fmtDur, fmtMoney, fmtTime, post, shortDigest, type Env, flyApp } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { Acts, Banner, Block, ClassChip, Kv, List, Pill, Seg, Table, Toggle, type Act, type Cell, type Col, type Row, type Tone } from '../ui/Block';
import { Icon } from '../ui/Icons';
import { Confirm, Drawer, useAdmin, type AdminCtx } from '../ui/Shell';
import { confirmSimple } from './dataShared';
import { RecordingDrawer } from './RecordingDrawer';

export type AppKey = 'engine' | 'engine-en' | 'api' | 'app';

export interface FlyEvent { id?: string; type?: string; status?: string; timestamp?: number; source?: string; request?: { reason?: string; exit_event?: { exit_code?: number; oom_killed?: boolean } } }
export interface Recording { id: string; state: string; transcription: string; listeners: number }

export interface Machine {
  id: string;
  name: string;
  app: string;
  state: string;
  region: string;
  image: string;
  imageDigest: string;
  release?: string | null;
  cpuKind: string;
  cpus: number;
  memoryMb: number;
  routable: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  stateSince?: string | null;
  envNames: string[];
  events: FlyEvent[];
  config: { services?: { autostart?: boolean; autostop?: boolean | string; concurrency?: { hard_limit?: number; soft_limit?: number; type?: string } }[] } & Record<string, unknown>;
  cpuClass?: string | null;
  cpu?: string | null;
  cpuAsOf?: string | null;
  recording?: Recording | null;
  pins?: number;
  spare?: Spare | null;
  session?: RtSession | null;
  updateWhenStopped?: boolean;
  ratePerHour?: number | null;
}

export interface Spare {
  pool: string; bootedAt: string; readyAt: string | null; claimedAt: string | null; claimedBy: string | null; endedAt: string | null; endReason: string | null;
  for: { principal: string; email: string | null; clientKind: string; spokenLanguage: string | null }[];
}

export interface RtSession { id: string; startedAt: string | null; endedAt: string | null; heldS: number | null; source: 'spare' | 'held' | 'cold' | null; spokenLanguage: string | null; email: string | null; keyName: string | null }
const SESSION_SOURCE: Record<string, string> = { spare: 'took the warm spare', held: 'on the engine its last dictation held', cold: 'cold start: no warm engine was ready' };
const sessionCell = (s: RtSession) => s.endedAt
  ? { t: `Held for the next dictation · ${Math.round(s.heldS ?? 0)} s left`, sub: `for ${s.email ?? 'a key'}${s.keyName ? ` · ${s.keyName}` : ''} · ${s.spokenLanguage ?? 'auto-detect'}`, fg: 'var(--conf-mid)' }
  : { t: `Realtime session · ${SESSION_SOURCE[s.source ?? ''] ?? 'started'}`, sub: `for ${s.email ?? 'a key'}${s.keyName ? ` · ${s.keyName}` : ''} · ${s.spokenLanguage ?? 'auto-detect'}`, fw: 500 };

const CLIENT_KIND: Record<string, string> = { mac_app: 'Mac Desktop App', dashboard: 'Web app', chrome_extension: 'Chrome extension', obs_plugin: 'OBS plugin', sdk: 'SDK', other: 'Other client' };
const spareFor = (s: Spare) => s.for.length ? s.for.map((w) => `${w.email ?? w.principal.slice(0, 8)} · ${CLIENT_KIND[w.clientKind] ?? w.clientKind}`).join(', ') : 'nobody recorded';
const spareOutcome = (s: Spare) => s.claimedAt ? `taken by ${s.claimedBy?.includes('-') ? `recording ${s.claimedBy.slice(0, 8)}` : 'a realtime session'}` : s.endedAt ? `ended: ${s.endReason ?? '—'}` : s.readyAt ? 'ready, waiting for a Start' : 'loading';
const spareOfRun = (m: Machine) => {
  const s = m.spare;
  if (!s || m.state !== 'started') return null;
  const since = m.stateSince ? new Date(m.stateSince).getTime() : 0;
  return new Date(s.bootedAt).getTime() >= since - 120_000 ? s : null;
};

export interface MachineList { app: string; machines: Machine[]; ageS: number; currentImage: string; flyError: string | null }

export type Busy = { kind: 'recording'; recordingId: string; state: string; transcription: string } | { kind: 'warm_pin'; principals: string[]; emails: string[] } | null;
export interface MachineDetail extends Machine {
  busy: Busy;
  boots: { at: string; cpuClass: string | null; cpu: string | null; region: string | null }[];
  recordingsServed: { id: string; transcription: string; startedAt: string | null; endedAt: string | null; endReason: string | null; wallDurationS: number | null }[];
  spares: Spare[];
}

export interface Attempt { n: number; forMachine: number; try: number; machineId: string | null; cpuClass: string | null; cpu: string | null; result: string; seconds: number | null; startedAt: string }
export interface EngineJob {
  app?: string;
  id: string; mode: 'create' | 'replace'; requested: number; region: string; requiredClass: string; attemptsPerMachine: number; leaveStopped: boolean; targets: string[];
  state: 'running' | 'done' | 'failed'; placed: number; kept: string[]; failed: string[]; error: string | null; startedBy: string | null; startedAt: string; endedAt: string | null; attempts: Attempt[];
}

export const appName = (env: Env, app: AppKey) => flyApp(env, app);
export const pl = (n: number, w: string) => `${n.toLocaleString()} ${w}${n === 1 ? '' : 's'}`;
export const secondsSince = (iso?: string | null) => (iso ? Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000) : 0);

export const notRoutable = (m: Machine) => !m.routable;
export const isOpenRecording = (r?: Recording | null) => !!r && ['pending', 'live', 'paused', 'processing'].includes(r.state);

export const STATE_TONE: Record<string, [Tone, string]> = { started: ['ok', 'started'], stopped: ['mute', 'stopped'], suspended: ['warn', 'suspended'], starting: ['info', 'starting'], stopping: ['info', 'stopping'], suspending: ['info', 'suspending'], created: ['info', 'created'], destroyed: ['bad', 'destroyed'], replacing: ['info', 'replacing'] };

export function runCost(m: Machine): number {
  if (m.state !== 'started' || !m.stateSince || !m.ratePerHour) return 0;
  return (secondsSince(m.stateSince) / 3600) * m.ratePerHour;
}

export const sizeOf = (m: Pick<Machine, 'cpuKind' | 'cpus' | 'memoryMb'>) => `${m.cpuKind}-${m.cpus}x · ${m.memoryMb >= 1024 ? `${Math.round(m.memoryMb / 1024)} GB` : `${m.memoryMb} MB`}`;

export const stale = (ageS: number, everyS: number) => ageS > 2 * everyS;

export function useMachineActions(app: AppKey, reload: () => void) {
  const ctx = useAdmin();
  const { env } = ctx;
  const [probing, setProbing] = useState<Set<string>>(new Set());
  const name = appName(env, app);
  const base = (id: string) => `/admin/machines/${app}/${id}`;

  const fail = (e: unknown, what: string) => {
    const err = e as ApiErr;
    ctx.toast(`${what} failed`, err?.message ?? String(e), { logged: false });
  };

  const start = async (m: Machine) => {
    try {
      await post(env, `${base(m.id)}/start`);
      reload();
      ctx.toast(`Starting ${m.id}`, `${name}${app === 'engine' ? ' · class is re-read at boot' : ''}`, { undo: () => void post(env, `${base(m.id)}/stop`).then(reload).catch((e) => fail(e, 'Undo')) });
    } catch (e) { fail(e, 'Start'); }
  };

  const doStop = async (m: Machine, to: 'stop' | 'suspend', reason: string) => {
    await post(env, `${base(m.id)}/${to}`, reason ? { reason } : {});
    reload();
    const verb = to === 'suspend' ? 'Suspended' : 'Stopped';
    const sub = reason ? `Ended ${m.recording?.id ?? 'its recording'} · ${reason}` : `${name}${m.updateWhenStopped ? ' · it takes the current image now that it has stopped' : ''}`;
    ctx.toast(`${verb} ${m.id}`, sub, reason ? {} : { undo: () => void post(env, `${base(m.id)}/start`).then(reload).catch((e) => fail(e, 'Undo')) });
  };

  const stop = (m: Machine, to: 'stop' | 'suspend') => {
    const r = m.recording;
    const verb = to === 'suspend' ? 'Suspend' : 'Stop';
    if (isOpenRecording(r)) {
      ctx.openModal(
        <Confirm env={env} app={name} mobile={ctx.mobile} danger title={`${verb} ${m.id}`} typed={m.id}
          body={`This machine holds ${r!.id} (${r!.transcription}${r!.state === 'live' ? `, on air with ${pl(r!.listeners, 'listener')}` : `, ${r!.state}`}). ${verb === 'Stop' ? 'Stopping' : 'Suspending'} it ends the recording and tells listeners the host ended it.`}
          reason={{ label: 'Reason', placeholder: 'Written to the audit entry' }}
          confirmLabel={`${verb} and end ${r!.id.slice(0, 8)}`}
          onConfirm={(reason) => doStop(m, to, reason)} onClose={ctx.closeModal} />,
      );
      return;
    }
    confirmSimple(ctx, { title: `${verb} ${m.id}`, app: name, danger: true, body: `${verb === 'Stop' ? 'Stops' : 'Suspends'} the machine; no recording is open on it.`, confirmLabel: verb, onConfirm: async () => {
      void doStop(m, to, '').catch((e) => {
      const err = e as ApiErr;
      if (err.status === 409) {
        ctx.openModal(
          <Confirm env={env} app={name} mobile={ctx.mobile} danger title={`${verb} ${m.id}`} typed={m.id} body={err.message} reason={{ label: 'Reason', placeholder: 'Written to the audit entry' }} confirmLabel={`${verb} ${m.id}`} onConfirm={(reason) => doStop(m, to, reason)} onClose={ctx.closeModal} />,
        );
      } else fail(e, verb);
    }); } });
  };

  const forceStop = (m: Machine) => {
    const r = m.recording!;
    ctx.openModal(
      <Confirm env={env} app={name} mobile={ctx.mobile} danger title={`Force stop ${r.id.slice(0, 8)}`} typed={r.id}
        body={`Ends ${r.id} (${r.transcription}${r.state === 'live' ? `, on air with ${pl(r.listeners, 'listener')}` : `, ${r.state}`}) on ${m.id}. Listeners are told the host ended it; the machine stays as it is and finalize runs as usual.`}
        reason={{ label: 'Reason', placeholder: 'Written to the audit entry' }} confirmLabel="Force stop"
        onConfirm={async (reason) => { await post(env, `/admin/recordings/${r.id}/force-stop`, { reason, confirm: r.id }); reload(); ctx.toast(`Force-stopped ${r.id.slice(0, 8)}`, reason); }}
        onClose={ctx.closeModal} />,
    );
  };

  const probe = async (m: Machine) => {
    setProbing((s) => new Set(s).add(m.id));
    ctx.toast(`Probing ${m.id}`, 'start without service → read class → persist → stop', { logged: false });
    try {
      const p = await post<{ cpuClass: string | null; cpu: string | null; ready: boolean }>(env, `${base(m.id)}/probe`);
      reload();
      ctx.toast(`Probed ${m.id}: ${p.cpuClass ?? 'class not read'}`, `${p.cpu ?? 'no CPU descriptor'} · left stopped`);
    } catch (e) { fail(e, 'Probe'); } finally {
      setProbing((s) => { const n = new Set(s); n.delete(m.id); return n; });
    }
  };

  const destroyConfirm = (m: Machine, afterClear?: number) => {
    ctx.openModal(
      <Confirm env={env} app={name} mobile={ctx.mobile} deskOnly danger title="Destroy machine" typed={m.id} confirmLabel={`Destroy ${m.id}`}
        body={`The machine is stopped and idle. Destroying it removes it from Fly${app === 'engine' ? ' and clears any warm pin that points at it' : ''}. Its config is saved to the audit entry so it can be recreated.`}
        list={[['Image', shortDigest(m.imageDigest)], ['Size', sizeOf(m)], ['Region', m.region], ['CPU class', m.cpuClass ?? 'unprobed']]}
        onConfirm={async () => {
          await post(env, `${base(m.id)}/destroy`, { confirm: m.id });
          reload();
          ctx.toast(`Destroyed ${m.id}`, `Config saved to the audit entry${afterClear ? ` · ${pl(afterClear, 'warm pin')} cleared` : ''}`);
        }}
        onClose={ctx.closeModal} />,
    );
  };

  const destroy = (m: Machine) => {
    if (isOpenRecording(m.recording)) {
      ctx.openModal(<Confirm env={env} app={name} mobile={ctx.mobile} title="Destroy refused" body={`${m.id} is referenced by ${m.recording!.id} (${m.recording!.transcription}). Destroy is refused while a recording references the machine.`} cancelLabel="Close" onClose={ctx.closeModal} />);
      return;
    }
    if (m.pins) {
      ctx.openModal(
        <Confirm env={env} app={name} mobile={ctx.mobile} deskOnly danger title="Destroy refused" body={`${m.id} is the warm pin for ${pl(m.pins, 'principal')}. Clear the pins first; the next stream from each goes to any free engine.`} confirmLabel="Clear pins and destroy" cancelLabel="Close"
          onConfirm={async () => { const r = await post<{ cleared: number }>(env, `${base(m.id)}/clear-pins`); ctx.closeModal(); setTimeout(() => destroyConfirm(m, r.cleared), 50); }}
          onClose={ctx.closeModal} />,
      );
      return;
    }
    destroyConfirm(m);
  };

  const acts = (m: Machine, inDrawer = false): Act[] => {
    const a: Act[] = [];
    const desk = ctx.mobile;
    if (notRoutable(m) || probing.has(m.id)) return a;
    const r = isOpenRecording(m.recording) ? m.recording : null;
    if (m.state === 'stopped' || m.state === 'suspended') a.push({ label: 'Start', onClick: () => confirmSimple(ctx, { title: `Start ${m.id}`, app: name, body: 'The machine boots and is billed while it runs.', confirmLabel: 'Start', onConfirm: () => start(m) }) });
    if (m.state === 'started') {
      a.push({ label: 'Stop', onClick: () => stop(m, 'stop') });
      if (app !== 'engine' || !r) a.push({ label: 'Suspend', onClick: () => stop(m, 'suspend'), disabled: desk, title: desk ? 'Desktop-only' : undefined });
    }
    if (m.state === 'suspended') a.push({ label: 'Stop', onClick: () => stop(m, 'stop') });
    if (r) a.push({ label: 'Force stop', danger: true, onClick: () => forceStop(m), title: `Ends ${r.id}; requires the typed recording id and a reason` });
    if (m.state === 'stopped' && (app === 'engine' || app === 'engine-en')) a.push({ label: 'Probe CPU', onClick: () => confirmSimple(ctx, { title: `Probe ${m.id}`, app: name, body: 'Starts the machine without its service, reads the CPU class, saves it and stops it again.', confirmLabel: 'Probe', onConfirm: () => probe(m) }), disabled: desk, title: 'Start without service, read class, persist, stop' });
    if (inDrawer && r) a.push({ label: 'Open recording', onClick: () => ctx.openDrawer(`rec:${r.id}`, <RecordingDrawer id={r.id} />, true) });
    if (m.state === 'stopped' && !r) a.push({ label: 'Destroy', danger: true, onClick: () => destroy(m), disabled: desk, title: desk ? 'Desktop-only' : undefined });
    return a;
  };

  return { acts, probing };
}

export const byAge = (ms: Machine[]) => [...ms].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));

export type Kind = 'engine' | 'api';

export const poolOf = (m: Pick<Machine, 'app'>) => (m.app.endsWith('-engine-en') ? 'English' : 'Multilingual');
export const poolKey = (m: Pick<Machine, 'app'>): 'engine' | 'engine-en' => (m.app.endsWith('-engine-en') ? 'engine-en' : 'engine');

export const sizeShort = (m: Pick<Machine, 'cpuKind' | 'cpus' | 'memoryMb'>) => ({ t: `${m.cpus}x`, sub: `${m.cpuKind} · ${m.memoryMb >= 1024 ? `${Math.round(m.memoryMb / 1024)} GB` : `${m.memoryMb} MB`}` });

export function machCols(kind: Kind): Col[] {
  const base: Col[] = [{ label: 'MACHINE', width: 'minmax(120px,1fr)' }];
  if (kind === 'engine') base.push({ label: 'POOL', width: 'minmax(100px,.7fr)' }, { label: 'SIZE', width: 'minmax(110px,.7fr)' });
  base.push({ label: 'STATE', width: 'minmax(110px,.9fr)' });
  base.push({ label: 'CPU CLASS', width: 'minmax(150px,.9fr)' });
  base.push({ label: 'IMAGE', width: 'minmax(130px,1fr)' });
  if (kind === 'engine') base.push({ label: 'CURRENT RECORDING', width: 'minmax(180px,2fr)' }, { label: 'ON AIR FOR', width: 'minmax(110px,1fr)' });
  else base.push({ label: 'SIZE', width: 'minmax(140px,1fr)' });
  base.push({ label: 'RUN COST', width: '72px', align: 'r' }, { label: '', width: 'minmax(120px,auto)', align: 'r' });
  return base;
}

export function machRow(m: Machine, kind: Kind, env: Env, acts: Act[], opts: { probing?: boolean; onOpen?: () => void }): Row {
  const cand = notRoutable(m) || !!opts.probing;
  const [tone, label] = STATE_TONE[m.state] ?? ['mute', m.state];
  const up = cand ? 'not routable' : m.state === 'started' ? `up ${fmtDur(secondsSince(m.stateSince))}` : m.stateSince ? `for ${fmtDur(secondsSince(m.stateSince))}` : '';
  const cells: Cell[] = [{ t: m.id, sub: `${m.region} · ${m.app.replace(env.appPrefix, '')}`, mono: true }];
  if (kind === 'engine') cells.push({ t: poolOf(m), fw: 500 }, { ...sizeShort(m), mono: true });
  cells.push(cand ? { dot: 'info', t: opts.probing || m.metadata?.vs_role === 'probe' ? 'probing' : 'candidate', sub: up, fg: 'var(--brand)' } : { dot: tone, hollow: tone === 'mute', t: label, sub: up });
  cells.push({ cls: m.cpuClass ?? null, sub: m.cpuAsOf ? `as of ${fmtDate(m.cpuAsOf)}` : undefined });
  cells.push(m.updateWhenStopped ? { pill: { label: 'update when it stops', tone: 'warn' }, sub: shortDigest(m.imageDigest) } : { t: shortDigest(m.imageDigest), sub: m.release ?? 'not in a recent release', mono: true });
  if (kind === 'engine') {
    const r = m.recording;
    const sp = spareOfRun(m);
    const rs = !cand && m.state === 'started' ? m.session : null;
    cells.push(r ? { t: r.transcription, sub: `${r.id} · ${r.state}`, fw: 500 } : rs ? sessionCell(rs) : sp && !cand ? { t: `Warm boot · ${spareOutcome(sp)}`, sub: `for ${spareFor(sp)}`, fg: 'var(--conf-mid)' } : cand ? { t: opts.probing || m.metadata?.vs_role === 'probe' ? 'Probing CPU' : 'Candidate', sub: 'no public service', fg: 'var(--ink-62)' } : { t: 'Idle', fg: 'var(--ink-45)' });
    cells.push(r && r.state === 'live' ? { pill: { label: 'ON AIR', tone: 'live' }, t: pl(r.listeners, 'listener') } : r ? { t: r.state, sub: 'off air' } : { t: '—', fg: 'var(--ink-40)' });
  } else cells.push({ t: sizeOf(m), sub: 'fixed per app', fg: 'var(--ink-80)' });
  const c = runCost(m);
  cells.push({ t: c ? fmtMoney(c) : '—', sub: c ? 'this run' : '', mono: true });
  cells.push({ acts });
  return { key: m.id, cells, onClick: cand ? undefined : opts.onOpen, bg: cand ? 'var(--brand-tint)' : undefined };
}

export function MachineDrawer({ app, id }: { app: AppKey; id: string }) {
  const ctx = useAdmin();
  const { env } = ctx;
  const d = useData<MachineDetail>(env, `/admin/machines/${app}/${id}`, 5, ctx.paused);
  const list = useData<MachineList>(env, `/admin/machines?app=${app}`, 5, ctx.paused);
  const { acts } = useMachineActions(app, () => { d.reload(); list.reload(); });
  const m = d.data;
  const listed = list.data?.machines.find((x) => x.id === id);
  const merged: MachineDetail | null = m ? { ...m, recording: listed?.recording ?? (m.busy?.kind === 'recording' ? { id: m.busy.recordingId, state: m.busy.state, transcription: m.busy.transcription, listeners: 0 } : null), pins: listed?.pins ?? (m.busy?.kind === 'warm_pin' ? m.busy.principals.length : 0), updateWhenStopped: listed?.updateWhenStopped, ratePerHour: listed?.ratePerHour, release: listed?.release } : null;
  const name = appName(env, app);
  const err = describeErr(d.error, 'Fly');
  const svc = m?.config?.services?.[0];
  const r = merged?.recording;

  return (
    <Drawer eyebrow={name.toUpperCase()} title={id} mono wide
      sub={merged ? `${merged.state === 'started' ? `started · up ${fmtDur(secondsSince(merged.stateSince))}` : merged.state} · ${merged.region} · ${sizeOf(merged)}` : d.loading ? 'Asking Fly…' : err?.title}
      chip={merged ? <ClassChip cls={merged.cpuClass} /> : undefined}
      acts={merged ? <Acts acts={acts(merged, true)} size="sm" /> : undefined}>
      {err && !m && <Block title="Machine" error={{ title: err.title, text: err.text, retry: d.reload }} />}
      {merged && r && isOpenRecording(r) && (
        <Block bg="var(--live-soft)"><Banner icon="signal" fg="var(--live)" text={`Holds ${r.id} · ${r.transcription}`} sub={`${r.state}${r.state === 'live' ? ` · ${pl(r.listeners, 'listener')}` : ''}`} acts={[{ label: 'Open recording', onClick: () => ctx.openDrawer(`rec:${r.id}`, <RecordingDrawer id={r.id} />, true) }]} /></Block>
      )}
      {merged && merged.busy?.kind === 'warm_pin' && (
        <Block bg="var(--conf-mid-soft)"><Banner icon="info" fg="var(--conf-mid)" text={`Warm pin for ${merged.busy.emails.filter(Boolean).join(', ') || pl(merged.busy.principals.length, 'principal')}`} sub="A returning host lands here while the pin lasts. Destroy is refused until it is cleared." /></Block>
      )}
      {merged?.updateWhenStopped && (
        <Block bg="var(--conf-mid-soft)"><Banner icon="tag" fg="var(--conf-mid)" text="Update when it stops" sub={`A deploy does not replace an engine holding a recording. It takes ${shortDigest(list.data?.currentImage.split('@')[1])} at its next stop.`} /></Block>
      )}
      {merged && (
        <Block title="Config" asOf={hhmmss(d.at)} stale={stale(d.ageS, 5)}>
          <Kv kw="34%" rows={[
            { k: 'Image', v: merged.updateWhenStopped ? { t: `${shortDigest(merged.imageDigest)} · will update to ${shortDigest(list.data?.currentImage.split('@')[1])} on next stop`, sub: imageSub(merged), mono: true, fg: 'var(--conf-mid)' } : { t: shortDigest(merged.imageDigest), sub: imageSub(merged), mono: true } },
            { k: 'Size', v: { t: sizeOf(merged), sub: 'fixed per app' } },
            { k: 'Region', v: { t: merged.region, mono: true } },
            { k: 'CPU', v: merged.cpuClass ? { t: merged.cpu ?? merged.cpuClass, sub: `${merged.cpuClass} · as of ${fmtDate(merged.cpuAsOf)}` } : { t: 'Not probed yet', sub: 'Probe CPU reads it without attaching the service', fg: 'var(--ink-55)' } },
            { k: 'Services', v: svc ? { t: `hard_limit = ${svc.concurrency?.hard_limit ?? '—'} · soft_limit = ${svc.concurrency?.soft_limit ?? '—'}`, sub: `${svc.concurrency?.type ?? ''} · ${merged.routable ? 'routable' : 'not routable: no public service'}`, mono: true } : { t: 'No public service', sub: 'not routable', fg: 'var(--conf-mid)' } },
            { k: 'When idle', v: svc ? { t: `auto_start = ${svc.autostart ? 'on' : 'off'} · auto_stop = ${svc.autostop === true ? 'stop' : svc.autostop || 'off'}`, mono: true } : { t: '—' } },
            { k: 'Created', v: { t: fmtDate(merged.createdAt) } },
          ]} />
        </Block>
      )}
      {merged && (
        <Block title="Environment" sub="Names only, never values" count={merged.envNames.length} empty={merged.envNames.length ? undefined : 'No runtime environment.'}>
          <List items={merged.envNames.map((k) => ({ key: k, t: <span className="mono" style={{ fontSize: 12 }}>{k}</span>, right: <Pill tone="ok">SET</Pill> }))} />
        </Block>
      )}
      {merged && (
        <Block title="Fly events" asOf={hhmmss(d.at)} empty={merged.events.length ? undefined : 'No events.'}>
          <List items={merged.events.map((e, i) => ({ key: e.id ?? String(i), t: <span style={{ fontWeight: 600 }}>{e.type ?? '—'}</span>, sub: `${e.status ?? ''}${e.source ? ` · ${e.source}` : ''}${e.request?.exit_event ? ` · exit ${e.request.exit_event.exit_code}${e.request.exit_event.oom_killed ? ' · OOM killed' : ''}` : ''}`, right: e.timestamp ? fmtDate(new Date(e.timestamp).toISOString()) : '' }))} />
        </Block>
      )}
      {merged && <Block note="The boot log is not available over the Machines API. Read it with `fly logs` on the engine app." />}
      {merged && app === 'engine' && (
        <Block title="Warm boots" sub="Each time it was kept loaded as the spare for someone's next Start, and who it was for." empty={m!.spares?.length ? undefined : 'Never a spare.'}>
          <Table cols={[{ label: 'BOOTED', width: 'minmax(110px,1fr)' }, { label: 'FOR', width: 'minmax(200px,2fr)' }, { label: 'OUTCOME', width: 'minmax(160px,1.4fr)' }]} mobile={ctx.mobile}
            rows={(m!.spares ?? []).map((s, i) => ({ key: `${s.bootedAt}-${i}`, cells: [{ t: fmtDate(s.bootedAt), mono: true, sub: s.pool }, { t: spareFor(s), sub: s.for.map((w) => w.spokenLanguage ?? 'auto-detect').join(', ') }, { t: spareOutcome(s), sub: s.endedAt ? fmtDate(s.endedAt) : '' }] }))} />
        </Block>
      )}
      {merged && app === 'engine' && (
        <Block title="Probe history" sub="Class as of each start. The host can change between starts." empty={m!.boots.length ? undefined : 'Never probed.'}>
          <Table cols={[{ label: 'START', width: 'minmax(110px,1fr)' }, { label: 'CLASS', width: '130px' }, { label: 'CPU', width: 'minmax(160px,1.6fr)' }]} mobile={ctx.mobile}
            rows={m!.boots.map((b, i) => ({ key: `${b.at}-${i}`, cells: [{ t: fmtDate(b.at), mono: true }, { cls: b.cpuClass }, { t: b.cpu ?? '—', sub: b.region ?? '' }] }))} />
        </Block>
      )}
      {merged && app === 'engine' && (
        <Block title="Recordings served" empty={m!.recordingsServed.length ? undefined : 'No recordings yet.'}>
          <Table cols={[{ label: 'RECORDING', width: '110px' }, { label: 'TRANSCRIPTION', width: 'minmax(140px,1.5fr)' }, { label: 'LENGTH', width: '70px', align: 'r' }, { label: 'END REASON', width: 'minmax(100px,1fr)' }]} mobile={ctx.mobile}
            rows={m!.recordingsServed.map((x) => ({ key: x.id, cells: [{ t: x.id.slice(0, 8), mono: true }, { t: x.transcription }, { t: x.wallDurationS != null ? fmtDur(x.wallDurationS) : x.startedAt ? fmtDur((new Date(x.endedAt ?? Date.now()).getTime() - new Date(x.startedAt).getTime()) / 1000) : '—', mono: true }, { t: x.endReason ?? (x.endedAt ? 'ended' : 'open'), mono: true }], onClick: () => ctx.openDrawer(`rec:${x.id}`, <RecordingDrawer id={x.id} />, true) }))} />
        </Block>
      )}
    </Drawer>
  );
}

export interface Odds { class: string; hits: number; attempts: number }

export function replaceSets(machines: Machine[]) {
  const real = machines.filter((m) => m.routable);
  const elig = real.filter((m) => m.state === 'stopped' && !!m.cpuClass && m.cpuClass !== 'vnni' && !isOpenRecording(m.recording) && !m.pins);
  const skip = real.filter((m) => m.cpuClass !== 'vnni' && !elig.includes(m)).map((m): [string, string] => [m.id, !m.cpuClass ? 'unprobed: probe it first' : isOpenRecording(m.recording) ? `holds ${m.recording!.id.slice(0, 8)}` : m.pins ? 'warm pin points at it' : `${m.state}, not stopped and idle`]);
  return { elig, skip };
}

const resultPill = (r: string): { label: string; tone: Tone } => (r === 'starting' ? { label: 'READING CLASS', tone: 'info' } : r === 'kept' ? { label: 'KEPT', tone: 'ok' } : r === 'destroyed' ? { label: 'DESTROYED', tone: 'mute' } : { label: 'FAILED', tone: 'bad' });

export function AttemptsTable({ job, mobile }: { job: EngineJob; mobile: boolean }) {
  const cols: Col[] = [{ label: '#', width: '32px' }, { label: 'MACHINE', width: 'minmax(120px,1fr)' }, { label: 'HOST', width: 'minmax(150px,1.3fr)' }, { label: 'CLASS', width: '130px' }, { label: 'RESULT', width: '130px' }, { label: 'SECS', width: '48px', align: 'r' }];
  const rows: Row[] = job.attempts.slice().reverse().map((a) => ({
    key: String(a.n), bg: a.result === 'starting' ? 'var(--brand-tint)' : undefined,
    cells: [
      { t: String(a.n), mono: true },
      { t: a.machineId ?? '—', sub: `${job.targets?.length ? `for ${job.targets[a.forMachine - 1]?.slice(0, 6) ?? '?'}… · ` : `machine ${a.forMachine} · `}try ${a.try}`, mono: true },
      a.result === 'starting' ? { t: 'created without service', sub: 'reading class', fg: 'var(--ink-55)' } : { t: a.cpu ?? '—', sub: a.result.startsWith('failed') ? a.result : '' },
      { cls: a.result === 'starting' ? null : a.cpuClass },
      { pill: resultPill(a.result) },
      { t: a.seconds != null ? String(Math.round(a.seconds)) : '—', mono: true },
    ],
  }));
  return <Table cols={cols} rows={rows} minWidth={640} mobile={mobile} />;
}

export function CreateModal({ mode: mode0, machines, jobId: jobId0 }: { mode: 'create' | 'replace'; machines: Machine[]; jobId?: string }) {
  const ctx = useAdmin();
  const { env } = ctx;
  const [mode] = useState(mode0);
  const [count, setCount] = useState(1);
  const [region, setRegion] = useState<'lax' | 'iad' | 'fra'>('lax');
  const [cls, setCls] = useState<'vnni' | 'avx2-zen3' | 'any'>('vnni');
  const [attempts, setAttempts] = useState(5);
  const [leaveStopped, setLeaveStopped] = useState(true);
  const [pool, setPool] = useState<'engine' | 'engine-en'>(() => (mode0 === 'replace' && !replaceSets(machines.filter((m) => poolKey(m) === 'engine')).elig.length && replaceSets(machines.filter((m) => poolKey(m) === 'engine-en')).elig.length ? 'engine-en' : 'engine'));
  const [jobId, setJobId] = useState<string | null>(jobId0 ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const odds = useData<Odds>(env, cls === 'any' ? null : `/admin/engines/odds?class=${cls}`, 0, ctx.paused, [cls]);
  const jobs = useData<{ jobs: EngineJob[] }>(env, '/admin/engines/jobs', 5, ctx.paused);
  const job = jobId ? jobs.data?.jobs.find((j) => j.id === jobId) ?? null : null;
  const running = jobs.data?.jobs.find((j) => j.state === 'running') ?? null;
  const repl = mode === 'replace';
  const name = job?.app ?? flyApp(env, pool);
  const rs = useMemo(() => replaceSets(machines.filter((m) => poolKey(m) === pool)), [machines, pool]);
  const size = pool === 'engine-en' ? 'performance-4x' : 'performance-8x';
  const n = repl ? rs.elig.length : count;
  const worst = n * attempts;
  const v = env.key === 'production' ? 'prod' : env.key === 'e2e' ? 'e2e' : 'local';
  const refuse = ctx.mobile ? 'Create-with-retry is desktop-only.' : running && running.id !== jobId ? `A ${running.mode} job is already running in ${env.label}. One at a time per environment.` : repl && !n ? 'No machine is eligible: every non-Genoa machine is busy, started, pinned or unprobed.' : '';
  const oddsText = cls === 'any' ? 'Any class is kept on the first attempt.' : odds.data ? (odds.data.attempts ? `Of the last ${odds.data.attempts} attempts, ${odds.data.hits} landed ${cls === 'vnni' ? 'Genoa' : cls}.` : 'No attempts yet.') : odds.error ? 'Odds not available.' : 'Reading the odds…';
  const expect = odds.data?.hits ? Math.min(worst, Math.ceil((n * odds.data.attempts) / odds.data.hits)) : worst;

  const start = async () => {
    setBusy(true); setErr(null);
    try {
      const body = { mode, count, region, requiredClass: cls, attemptsPerMachine: attempts, leaveStopped, pool };
      const r = await post<{ jobId: string }>(env, '/admin/engines/jobs', body);
      setJobId(r.jobId);
      jobs.reload();
      ctx.toast(`${repl ? 'Replace' : 'Create'} job started`, `${n} × ${cls} in ${region} · ${attempts} attempts per machine`);
    } catch (e) { setErr((e as ApiErr).message); } finally { setBusy(false); }
  };

  const done = job ? job.state !== 'running' : false;
  const pill = job ? (!done ? { label: 'RUNNING', tone: 'info' as Tone } : job.placed === job.requested ? { label: 'ALL PLACED', tone: 'ok' as Tone } : job.placed ? { label: 'PARTIALLY PLACED', tone: 'warn' as Tone } : { label: 'NONE PLACED', tone: 'bad' as Tone }) : null;
  const la = job?.attempts[job.attempts.length - 1];
  const lab = (p: ReactNode) => <span className="tbl__h" style={{ alignSelf: 'center' }}>{p}</span>;
  const stepper = (val: number, set: (n: number) => void, min: number, max: number) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--hair)', borderRadius: 8, overflow: 'hidden' }}>
      <button type="button" className="btn btn--sm" style={{ borderRadius: 0, width: 30 }} onClick={() => set(Math.max(min, val - 1))} aria-label="Fewer">−</button>
      <span className="mono" style={{ width: 32, textAlign: 'center', fontSize: 13, fontWeight: 600 }}>{val}</span>
      <button type="button" className="btn btn--sm" style={{ borderRadius: 0, width: 30 }} onClick={() => set(Math.min(max, val + 1))} aria-label="More">+</button>
    </div>
  );

  return (
    <div className="modal modal--wide" style={ctx.mobile ? { borderRadius: '18px 18px 0 0', width: '100%' } : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="adm__env" style={{ height: 22, padding: '0 8px', fontSize: 9.5, background: `var(--env-${v})`, color: `var(--env-${v}-ink)` }}><span className="adm__env-dot" style={{ width: 5, height: 5 }} />{env.short}</span>
        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-55)' }}>{name}</span>
      </div>
      <div className="modal__title">{repl ? 'Replace non-Genoa machines' : 'Create engines (retry until Genoa)'}</div>

      {!job && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: ctx.mobile ? '1fr' : '150px minmax(0,1fr)', gap: '12px 14px', alignItems: 'center' }}>
            {<>{lab('POOL')}<Seg value={pool} onChange={setPool} options={[{ value: 'engine', label: 'Multilingual' }, { value: 'engine-en', label: 'English (4x)' }]} /></>}
            {!repl && <>{lab('HOW MANY')}{stepper(count, setCount, 1, 5)}</>}
            {lab('REGION')}<Seg value={region} onChange={setRegion} mono options={[{ value: 'lax', label: 'lax' }, { value: 'iad', label: 'iad' }, { value: 'fra', label: 'fra' }]} />
            {lab('REQUIRED CLASS')}<Seg value={cls} onChange={setCls} mono options={[{ value: 'vnni', label: 'vnni' }, { value: 'avx2-zen3', label: 'avx2-zen3' }, { value: 'any', label: 'any class' }]} />
            {lab('ATTEMPTS PER MACHINE')}<div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>{stepper(attempts, setAttempts, 1, 10)}<span style={{ fontSize: 11.5, fontWeight: 300, color: 'var(--ink-55)' }}>fixed budget, then give up on that machine</span></div>
            {lab('LEAVE STOPPED')}<Toggle on={leaveStopped} onChange={setLeaveStopped} label="The proxy wakes machines on demand." />
          </div>
          {repl && (
            <div style={{ borderRadius: 10, background: 'var(--fill-05)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>Each replacement is created first; the old machine is destroyed only when the new one is Genoa. Pool size stays constant.</div>
              {rs.elig.map((m) => <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}><Icon name="check" size={14} style={{ color: 'var(--ok)' }} /><span className="mono">{m.id}</span><span style={{ color: 'var(--ink-55)' }}>{m.cpuClass} · stopped and idle</span></div>)}
              {rs.skip.map(([id, why]) => <div key={id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}><Icon name="close" size={14} style={{ color: 'var(--ink-40)' }} /><span className="mono">{id}</span><span style={{ color: 'var(--ink-55)' }}>{why}</span></div>)}
              {!rs.elig.length && !rs.skip.length && <div style={{ fontSize: 12, color: 'var(--ink-55)' }}>Every machine is already Genoa.</div>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, borderRadius: 10, background: 'var(--brand-tint)', border: '1px solid var(--brand-line)', padding: '10px 12px', fontSize: 12, lineHeight: 1.5, color: 'var(--ink-80)' }}>
            <Icon name="info" size={15} style={{ color: 'var(--brand)', flex: 'none', marginTop: 2 }} />
            <div>
              <b>Placement is not controllable.</b> Fly hands out whatever host it has for this machine size. Each candidate is created without its public service, so the proxy cannot route a stream to it while the job reads its class from /health. A matching machine gets the service attached and is kept; any other is destroyed. Each rejected attempt costs about one minute of {size}.
              <div style={{ marginTop: 6 }}>{oddsText} Worst case {worst} attempts ≈ {worst} min of {size}. At that rate, expect about {expect}.</div>
            </div>
          </div>
          {refuse && <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--conf-mid)' }}>{refuse}</div>}
          {err && <div className="login__err" role="alert"><Icon name="warn" size={15} style={{ color: 'var(--conf-low)', flex: 'none' }} /><span>{err}</span></div>}
          <div style={{ fontSize: 11.5, fontWeight: 300, color: 'var(--ink-55)' }}>Runs as a server-side job: closing this tab does not stop it. One create job at a time per environment.</div>
        </>
      )}

      {jobId && !job && (jobs.loading || !jobs.data ? <div style={{ fontSize: 12.5, color: 'var(--ink-62)' }}>Reading job {jobId}…</div> : <div style={{ fontSize: 12.5, color: 'var(--conf-mid)' }}>Job {jobId} is not in the last 20 jobs.</div>)}

      {job && pill && (
        <>
          <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-62)', wordBreak: 'break-word' }}>{job.id} · {job.requested} × {job.requiredClass} in {job.region} · {job.attemptsPerMachine} attempts per machine · {job.leaveStopped ? 'leave stopped' : 'leave started'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Pill tone={pill.tone}>{pill.label}</Pill>
            <span style={{ fontSize: 12.5, color: 'var(--ink-70)' }}>{done ? `Finished ${job.endedAt ? `at ${fmtTime(job.endedAt)}` : ''} after ${pl(job.attempts.length, 'attempt')}` : `Machine ${Math.min((la?.forMachine ?? 1), job.requested)} of ${job.requested} · attempt ${la?.try ?? 1} of ${job.attemptsPerMachine}${la ? ` · ${la.result === 'starting' ? 'created without service, reading class' : la.result === 'kept' ? 'kept, service attached' : la.result === 'destroyed' ? 'destroyed, next attempt' : la.result}` : ''}`}</span>
          </div>
          {done && (
            <div style={{ borderRadius: 10, background: 'var(--fill-05)', padding: '10px 12px' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{job.placed === job.requested ? 'All placed: ' : job.placed ? 'Partially placed: ' : 'None placed: '}{job.placed} of {job.requested} in {pl(job.attempts.length, 'attempt')}.</div>
              <div style={{ fontSize: 12, fontWeight: 300, color: 'var(--ink-62)', marginTop: 2 }}>{job.kept.length ? `${job.mode === 'replace' ? 'Replaced ' : 'Kept '}${job.kept.join(', ')}. ` : ''}{job.failed.length ? `Could not ${job.mode === 'replace' ? 'replace ' : 'place '}${job.failed.join(', ')}${job.mode === 'replace' ? ' (left in place).' : '.'}` : ''}{job.error ? ` ${job.error}` : ''}</div>
            </div>
          )}
          <Block title="Attempts" count={job.attempts.length} asOf={hhmmss(jobs.at)} stale={!done && stale(jobs.ageS, 5)} empty={job.attempts.length ? undefined : 'Creating the first machine without its public service…'}>
            <AttemptsTable job={job} mobile={ctx.mobile} />
          </Block>
        </>
      )}

      <div className="modal__acts">
        <button type="button" className="btn" style={{ height: 36 }} onClick={ctx.closeModal}>{job ? 'Close' : 'Cancel'}</button>
        {job && <button type="button" className="btn" style={{ height: 36 }} onClick={() => { ctx.closeModal(); ctx.go('jobs', { tab: 'create' }); }}>Open in Jobs</button>}
        {!job && <button type="button" className="btn btn--primary" style={{ height: 36 }} disabled={!!refuse || busy || !n} onClick={() => void start()}>{busy ? 'Starting…' : repl ? `Replace ${pl(n, 'machine')}` : 'Start job'}</button>}
      </div>
    </div>
  );
}

export function JobBanner({ job, machines, ctx }: { job: EngineJob; machines: Machine[]; ctx: AdminCtx }) {
  return (
    <Block bg="var(--brand-tint)" style={{ borderColor: 'var(--brand-line)' }}>
      <Banner icon="cpu" fg="var(--brand)" text={`${job.mode === 'replace' ? 'Replace' : 'Create'} job ${job.id.slice(0, 8)} is running on the server`}
        sub={`Attempt ${job.attempts.length} · machine ${Math.min(job.attempts[job.attempts.length - 1]?.forMachine ?? 1, job.requested)} of ${job.requested} · ${job.placed} placed. Candidates have no public service until kept. Closing this tab does not stop it.`}
        acts={[{ label: 'Watch', onClick: () => ctx.openModal(<CreateModal mode={job.mode} machines={machines} jobId={job.id} />) }]} />
    </Block>
  );
}

export interface Analytics { columns: string[]; rows: unknown[][]; from: string; to: string; panel: string }

export function useFallbackRate(env: Env, paused: boolean) {
  const from = useMemo(() => new Date(Date.now() - 7 * 86400_000).toISOString(), []);
  const d = useData<Analytics>(env, `/admin/analytics/translation?from=${encodeURIComponent(from)}`, 0, paused);
  let hit = 0, miss = 0, fb = 0, errs = 0;
  for (const r of d.data?.rows ?? []) {
    const [, outcome, count] = r as [string, string, number];
    const n = Number(count) || 0;
    if (outcome === 'hit') hit += n; else if (outcome === 'miss') miss += n; else if (String(outcome).startsWith('fallback')) fb += n; else errs += n;
  }
  const total = hit + miss + fb;
  return { ...d, hit, miss, fb, errs, total, rate: total ? (fb / total) * 100 : null };
}

export function fallbackCell({ fr, override }: { fr: ReturnType<typeof useFallbackRate>; override: string }): Cell {
  if (fr.error) return { t: 'Fallback rate not available', sub: describeErr(fr.error)?.title, fg: 'var(--conf-mid)' };
  if (fr.loading) return { t: 'Reading the last 7 days…', fg: 'var(--ink-55)' };
  if (fr.rate === null) return { t: 'No translations in the last 7 days', sub: `override: ${override}`, fg: 'var(--ink-62)' };
  return { t: `OpenAI fallback: ${fr.rate.toFixed(fr.rate < 10 ? 1 : 0)}% of translations in the last 7 days`, sub: `${fr.fb.toLocaleString()} of ${fr.total.toLocaleString()} · override: ${override}`, fg: fr.rate >= 10 ? 'var(--conf-mid)' : 'var(--ink-96)' };
}

export function clearPin(env: Env, ctx: AdminCtx, principalId: string, who: string, reload: () => void) {
  confirmSimple(ctx, { title: `Clear the warm pin for ${who}`, app: 'warm pins', danger: true, body: 'Their next stream goes to any free engine.', confirmLabel: 'Clear pin', onConfirm: async () => {
    try {
      await del(env, `/admin/warm/${principalId}`);
      reload();
      ctx.toast(`Cleared warm pin for ${who}`, 'Next stream goes to any free engine');
    } catch (e) {
      ctx.toast('Clear pin failed', (e as ApiErr).message, { logged: false });
    }
  } });
}

export function useTick() {
  const [, set] = useState(0);
  const ref = useRef(0);
  useEffect(() => { const id = setInterval(() => set(++ref.current), 1000); return () => clearInterval(id); }, []);
}

function imageSub(m: { image: string; release?: string | null }): string {
  return `${m.release ?? 'not in a recent release'} · stored as ${m.image.split('/').pop()?.split('@')[0] ?? m.image}`;
}
